import React, { useState, useEffect } from "react";
import { useTenant } from "../../hooks/useTenant";
import { Button, Field, Alert, Swatches } from "../../components";
import "./Settings.css";

// Branding. These fields land on customer-facing reports, so the panel on the right is
// a live preview rather than a description of what will happen.
//
// brand_colors is a JSON array of hex strings, validated server-side against
// /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/ with a maximum of eight entries. Anything that
// fails that filter is dropped silently on save, so the input validates locally first.

const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const SUGGESTED = ["#1f7fe5", "#093070", "#26814f", "#b83b4c", "#d99423", "#0b0d11"];

const BrandingScreen = ({ isAdmin }) => {
  const { tenant, loading, error, saving, saved, save } = useTenant();
  const [form, setForm] = useState(null);
  const [colorDraft, setColorDraft] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (tenant && !form) {
      setForm({
        tenant_name: tenant.tenant_name || "",
        website: tenant.website || "",
        address: tenant.address || "",
        contact_email: tenant.contact_email || "",
        contact_phone: tenant.contact_phone || "",
        report_tagline: tenant.report_tagline || "",
        brand_colors: Array.isArray(tenant.brand_colors) ? tenant.brand_colors : [],
      });
    }
  }, [tenant, form]);

  if (loading || !form) return <div className="v2-page"><p className="v2-hint">Loading…</p></div>;

  const primary = form.brand_colors[0] || "#1f7fe5";
  const addColor = (hex) => {
    if (!HEX.test(hex) || form.brand_colors.includes(hex) || form.brand_colors.length >= 8) return;
    setForm((f) => ({ ...f, brand_colors: [...f.brand_colors, hex] }));
    setColorDraft("");
  };

  return (
    <div className="v2-page">
      <div className="v2-pagehead">
        <div>
          <h2>Branding</h2>
          <p>How your workspace appears on the reports clients receive.</p>
          <p className="v2-hint" style={{ marginTop: ".25rem" }}>
            These are the defaults. An individual lead-magnet widget can override any of
            them on its own Report branding tab.
          </p>
        </div>
      </div>

      {!isAdmin && <Alert tone="warning">Only administrators can save these settings. You can look, but Save will be refused.</Alert>}
      <Alert>{error}</Alert>
      {saved && <Alert tone="success">{saved}</Alert>}

      <div className="v2-branding">
        <div className="v2-settings-form">
          <Field label="Workspace name"><input className="v2-input" value={form.tenant_name} onChange={set("tenant_name")} /></Field>
          <Field label="Website"><input className="v2-input" value={form.website} onChange={set("website")} placeholder="example.com" /></Field>
          <Field label="Address"><input className="v2-input" value={form.address} onChange={set("address")} /></Field>
          <Field label="Contact email"><input className="v2-input" type="email" value={form.contact_email} onChange={set("contact_email")} /></Field>
          <Field label="Contact phone"><input className="v2-input" value={form.contact_phone} onChange={set("contact_phone")} /></Field>
          <Field label="Report tagline" hint="Shown on the report cover under your name.">
            <input className="v2-input" value={form.report_tagline} onChange={set("report_tagline")} maxLength={255} />
          </Field>
          {/* Report style is not here. It is not one look for the workspace: a lead-magnet
              widget sets it on its own Report branding tab and a prospect scan picks it
              per scan, so a workspace-wide control only ever fought with them. Everything
              with no choice of its own renders as 'pdf'. The column is untouched and the
              tenant PUT still COALESCEs, so a value set before this stays where it is. */}

          <Field label="Brand colours" hint="Up to eight. The first is used as the primary on report covers.">
            <Swatches colors={form.brand_colors.length ? form.brand_colors : SUGGESTED} value={primary}
                      onChange={(c) => setForm((f) => ({ ...f, brand_colors: [c, ...f.brand_colors.filter((x) => x !== c)] }))} />
            <div className="v2-addrow" style={{ marginTop: ".5rem" }}>
              <input className="v2-input" value={colorDraft} onChange={(e) => setColorDraft(e.target.value)}
                     placeholder="#1F7FE5" maxLength={7}
                     onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addColor(colorDraft.trim()); } }} />
              <Button onClick={() => addColor(colorDraft.trim())} disabled={!HEX.test(colorDraft.trim()) || form.brand_colors.length >= 8}>
                Add
              </Button>
            </div>
            {form.brand_colors.length > 0 && (
              <div className="v2-colorlist">
                {form.brand_colors.map((c) => (
                  <span key={c} className="v2-chip">
                    <span className="v2-colordot" style={{ background: c }} />{c}
                    <button type="button" className="v2-chip-x" aria-label={`Remove ${c}`}
                            onClick={() => setForm((f) => ({ ...f, brand_colors: f.brand_colors.filter((x) => x !== c) }))}>✕</button>
                  </span>
                ))}
              </div>
            )}
          </Field>

          <div>
            <Button variant="primary" onClick={() => save(form)} disabled={saving || !isAdmin}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>

        <div className="v2-brandpreview">
          <div className="v2-section-title" style={{ marginBottom: ".5rem" }}>Report preview</div>
          <div className="v2-reportcard">
            <div className="v2-reportcard-band" style={{ background: primary }} />
            <div className="v2-reportcard-body">
              <div className="v2-reportcard-title" style={{ color: primary }}>{form.tenant_name || "Your workspace"}</div>
              {form.report_tagline && <div className="v2-reportcard-tag">{form.report_tagline}</div>}
              <div className="v2-reportcard-lines">
                <span style={{ width: "80%" }} /><span style={{ width: "95%" }} /><span style={{ width: "62%" }} />
              </div>
              <div className="v2-reportcard-foot">
                {[form.contact_email, form.contact_phone, form.website].filter(Boolean).join("  ·  ") || "Contact details appear here"}
              </div>
            </div>
          </div>
          <p className="v2-hint" style={{ marginTop: ".5rem" }}>
            Clients never see the product blue unless you choose it.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BrandingScreen;
